import {Box, Button, IconButton, MenuItem, Stack, TextField} from '@mui/material';
import {useCanAll, useCan, usePersona} from 'src/permissions/hooks';
import {Delete, PersonOutlineOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useState} from 'react';
import {ResponsiveTable} from '../../ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {getEvDriverNameFromBaseUser} from 'src/utils/userNameFormatter';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {FilterBar} from 'src/components/common/FilterBar';
import {FormattedMessage, useIntl} from 'react-intl';
import {GridColumns} from '@mui/x-data-grid';
import {ReManagersTableProps} from './ReManagersTable.types';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {EditIcon} from 'src/components/icons/EditIcon';
import theme from 'src/theme';
import {usersApi} from 'src/api';
import AMPModal from '../../AMPModal';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {isErrorObject, processedLanguages} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {BaseUserInfo} from 'src/types';
import {LanguageEnum} from 'src/enums/common';

const actionButtonSx = {
    width: '28px',
    height: '28px',
    p: '2px',
    color: theme.palette.neutral[400],
    backgroundColor: 'transparent',
    '.MuiSvgIcon-root': {
        fill: theme.palette.neutral[400],
    },
    '&:not(:last-child)': {
        mr: 1,
    },
};

const calculateColumnConfigUsers = (
    onUpdate: (user: BaseUserInfo) => void,
    onDelete: (user: BaseUserInfo) => void,
    canUpdate: boolean,
    canDelete: boolean
): GridColumns => {
    const columns: GridColumns = [
        {
            field: 'firstName',
            renderHeader: () => <FormattedMessage id={'firstName'}/>,
            flex: 2,
            minWidth: 200,
            sortable: false,
            cellClassName: 'ampTableCell',
            renderCell: (params) => <Box>{params.row.firstName}</Box>,
        },
        {
            field: 'lastName',
            renderHeader: () => <FormattedMessage id={'lastName'}/>,
            flex: 2,
            minWidth: 200,
            sortable: false,
            cellClassName: 'ampTableCell',
            renderCell: (params) => <Box>{params.row.lastName}</Box>,
        },
        {
            field: 'emailAddress',
            renderHeader: () => <FormattedMessage id={'emailAddress'}/>,
            flex: 2,
            minWidth: 200,
            sortable: false,
            cellClassName: 'ampTableCell',
            renderCell: (params) => <Box>{params.row.emailAddress}</Box>,
        },
    ];

    columns.push({
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        cellClassName: 'ampTableCell justifyEnd',
        renderCell: (params) => params.row.business && (
            <Stack direction="row" justifyContent="flex-end">
                <IconButton
                    sx={actionButtonSx}
                    onClick={() => onUpdate(params.row)}
                >
                    <EditIcon/>
                </IconButton>
                <IconButton
                    sx={actionButtonSx}
                    onClick={() => onDelete(params.row)}
                >
                    <Delete sx={{width: '20px'}}/>
                </IconButton>
            </Stack>
        ),
    });

    return columns;
};

export const ReManagersTable: FC<ReManagersTableProps> = ({
                                                              data,
                                                              isLoading,
                                                              total,
                                                              tableParams,
                                                              title,
                                                              icon,
                                                              addAction,
                                                              setTableParams,
                                                          }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const [updateManager] = usersApi.useUpdateManagerMutation();
    // The pair the endpoint still asks for: it stopped rebinding roles, but who may edit a manager is
    // a policy question, so the requirement stands until it is answered.
    const canUpdateManager = useCanAll('users.update', 'accessControl.manageBindings');
    const canDeleteManager = useCan('users.delete');
    const [deleteManager] = usersApi.useDeleteManagerMutation();

    // Modal states
    const [selectedUser, setSelectedUser] = useState<BaseUserInfo | null>(null);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [updateFirstName, setUpdateFirstName] = useState('');
    const [updateLastName, setUpdateLastName] = useState('');
    const [updateLanguage, setUpdateLanguage] = useState<LanguageEnum>(LanguageEnum.ENGLISH);

    const handlePerPageChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +e.target.value;
        if (typeof val === 'number') {
            setTableParams({size: val, page: 0});
        }
    };

    const handleChangePage = (e: number) => {
        setTableParams((prevState) => ({
            ...prevState,
            page: e,
        }));
    };

    const handleOpenUpdateModal = (user: BaseUserInfo) => {
        setSelectedUser(user);
        setUpdateFirstName(user.firstName);
        setUpdateLastName(user.lastName);
        setUpdateLanguage(user.language ?? LanguageEnum.ENGLISH);
        setIsUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedUser(null);
        setUpdateFirstName('');
        setUpdateLastName('');
        setUpdateLanguage(LanguageEnum.ENGLISH);
    };

    const handleUpdate = async () => {
        if (!selectedUser || !updateFirstName.trim() || !updateLastName.trim()) return;

        try {
            dispatch(preloaderChangeStatus(true));
            await updateManager({
                userId: selectedUser.id,
                data: {
                    businessId,
                    firstName: updateFirstName.trim(),
                    lastName: updateLastName.trim(),
                    language: updateLanguage,
                },
            }).unwrap();
            NotifySuccess(intl.formatMessage({id: 'updatedSuccessfully'}));
            handleCloseUpdateModal();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleDelete = async (user: BaseUserInfo) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await deleteManager({
                userId: user.id,
                businessId,
            }).unwrap();
            NotifySuccess(intl.formatMessage({id: 'deletedSuccessfully'}));
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const getColumnConfigUsers = (): GridColumns => {
        const columns = calculateColumnConfigUsers(
            handleOpenUpdateModal,
            handleDelete,
            canUpdateManager,
            canDeleteManager
        );
        // No slice for the platform administrator. It dropped the actions column from the one
        // account that certainly may edit and delete a manager; the column's own controls already
        // gate on canUpdateManager and canDeleteManager.
        return columns;
    };

    if (!data) {
        return null;
    }

    return (
        <>
            <TableLayout
                toolbar={(
                    <FilterBar
                        title={title}
                        icon={icon}
                        subtitle={intl.formatMessage({id: 'rowsFound'}, {count: data.length})}
                        actions={addAction}
                    />
                )}
                totalPages={total}
                page={tableParams.page}
                pageSize={tableParams.size}
                onPageChange={handleChangePage}
                onPageSizeChange={handlePerPageChange}
            >
                <ResponsiveTable
                    responsiveElement={
                        <CardList>
                            {data.map((item) => (
                                <TableCard key={item.id}>
                                    {/* The name through the shared formatter rather than two
                                        rows of its own, and the address they are reached at
                                        under it - which is what tells two managers apart. */}
                                    <PhoneCardHead
                                        person
                                        icon={<PersonOutlineOutlined/>}
                                        title={getEvDriverNameFromBaseUser(item)}
                                        subtitle={item.emailAddress}
                                    />
                                </TableCard>
                            ))}
                        </CardList>
                    }
                    desktopElement={
                        <Table
                            loading={isLoading}
                            rows={data}
                            hideFooter
                            columns={getColumnConfigUsers()}
                        />
                    }
                />
            </TableLayout>

            {/* Update Modal */}
            <AMPModal
                handleClose={handleCloseUpdateModal}
                open={isUpdateModalOpen}
                title={intl.formatMessage({id: 'editReManager'})}
                type="sm"
                fullWidth
            >
                <Box sx={{p: 3}}>
                    <TextField
                        fullWidth
                        label={intl.formatMessage({id: 'firstName'})}
                        value={updateFirstName}
                        onChange={(e) => setUpdateFirstName(e.target.value)}
                        sx={{mb: 2}}
                    />
                    <TextField
                        fullWidth
                        label={intl.formatMessage({id: 'lastName'})}
                        value={updateLastName}
                        onChange={(e) => setUpdateLastName(e.target.value)}
                        sx={{mb: 2}}
                    />
                    <TextField
                        select
                        fullWidth
                        label={intl.formatMessage({id: 'language'})}
                        value={updateLanguage}
                        onChange={(e) => setUpdateLanguage(e.target.value as LanguageEnum)}
                        sx={{mb: 3}}
                    >
                        {processedLanguages(intl).map((item) => (
                            <MenuItem
                                key={item.id}
                                value={item.value}
                            >
                                {item.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                        <Button variant="outlined" onClick={handleCloseUpdateModal}>
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdate}
                            disabled={!updateFirstName.trim() || !updateLastName.trim()}
                        >
                            <FormattedMessage id="save"/>
                        </Button>
                    </Box>
                </Box>
            </AMPModal>
        </>
    );
};
